import React, { useEffect, useState } from 'react';
import { supabase } from '../../../services/supabaseClient';

const VisaProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2500);
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.from('visa_products').select('*').order('country');
      if (error) throw error;
      setProducts(data || []);
    } catch (e) {
      setError('Failed to load visa products');
    } finally {
      setLoading(false);
    }
  };

  const loadRequirements = async (productId) => {
    setSelected(productId || null);
    if (!productId) { setRequirements([]); return; }
    const { data } = await supabase.from('visa_requirements').select('*').eq('visa_product_id', productId).order('mandatory', { ascending: false });
    setRequirements(data || []);
  };

  useEffect(() => { load(); }, []);

  const [pForm, setPForm] = useState({ country: '', visa_type: '', name: '', description: '', base_fee: '', currency: 'USD' });
  const [rForm, setRForm] = useState({ requirement_key: '', label: '', description: '', mandatory: true });

  const addProduct = async () => {
    if (!pForm.country.trim() || !pForm.name.trim()) return showToast('error', 'Country and name required');
    const payload = { ...pForm, base_fee: pForm.base_fee ? Number(pForm.base_fee) : null };
    const { error } = await supabase.from('visa_products').insert([payload]);
    if (error) return showToast('error', 'Failed to add product');
    setPForm({ country: '', visa_type: '', name: '', description: '', base_fee: '', currency: 'USD' });
    showToast('success', 'Product added');
    await load();
  };

  const addRequirement = async () => {
    if (!selected) return showToast('error', 'Select a product');
    if (!rForm.requirement_key.trim() || !rForm.label.trim()) return showToast('error', 'Key and label required');
    const payload = { ...rForm, visa_product_id: selected };
    const { error } = await supabase.from('visa_requirements').insert([payload]);
    if (error) return showToast('error', 'Failed to add requirement');
    setRForm({ requirement_key: '', label: '', description: '', mandatory: true });
    showToast('success', 'Requirement added');
    await loadRequirements(selected);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-900 font-mono text-white flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">🛂 Visa Products</h2>
        <p className="text-gray-400">Comprehensive visa product and requirement management</p>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded border text-sm ${toast.type === 'success' ? 'bg-green-900/20 border-green-800 text-green-400' : toast.type === 'error' ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-blue-900/20 border-blue-800 text-blue-400'}`}>
          {toast.text}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-900/20 border border-red-800 text-red-400 p-4 rounded mb-6">
          <p>{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Add Product */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4 text-white">➕ Add New Product</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Country" value={pForm.country} onChange={(e) => setPForm({ ...pForm, country: e.target.value })} />
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Visa type (study/work)" value={pForm.visa_type} onChange={(e) => setPForm({ ...pForm, visa_type: e.target.value })} />
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Product name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} />
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Currency" value={pForm.currency} onChange={(e) => setPForm({ ...pForm, currency: e.target.value })} />
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Base fee" value={pForm.base_fee} onChange={(e) => setPForm({ ...pForm, base_fee: e.target.value.replace(/[^0-9.]/g, '') })} />
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Description" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} />
          </div>
          <button onClick={addProduct} className="w-full mt-4 px-3 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Add Product</button>
        </div>

        {/* Products */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4 text-white">📋 Visa Products</h3>
          <div className="space-y-3">
            {products.map(p => (
              <button key={p.id} onClick={() => loadRequirements(p.id)} className={`w-full px-4 py-3 text-sm border rounded-lg text-left transition-colors ${selected === p.id ? 'bg-blue-900/20 border-blue-500 text-blue-400' : 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{p.name}</span>
                  {selected === p.id && <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">Selected</span>}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {p.country} • {p.visa_type || '-'}
                </div>
              </button>
            ))}
            {products.length === 0 && (
              <div className="text-center text-gray-500 py-8 bg-gray-900/50 rounded border-2 border-dashed border-gray-700">📭 No products available</div>
            )}
          </div>
        </div>
      </div>

      {/* Requirements */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mt-4">
        <h3 className="text-xl font-bold mb-4 text-white">📝 Requirements {selected ? '(selected product)' : ''}</h3>
        <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Key" value={rForm.requirement_key} onChange={(e) => setRForm({ ...rForm, requirement_key: e.target.value })} />
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Label" value={rForm.label} onChange={(e) => setRForm({ ...rForm, label: e.target.value })} />
            <input className="px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Description" value={rForm.description} onChange={(e) => setRForm({ ...rForm, description: e.target.value })} />
            <label className="flex items-center justify-center gap-2 px-3 py-2 border border-gray-600 rounded cursor-pointer bg-gray-700">
              <input type="checkbox" checked={rForm.mandatory} onChange={(e) => setRForm({ ...rForm, mandatory: e.target.checked })} className="rounded border-gray-600 bg-gray-700 text-blue-500" />
              <span className="font-medium text-white">Mandatory</span>
            </label>
          </div>
          <button onClick={addRequirement} className="w-full mt-4 px-3 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Add Requirement</button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Key</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Label</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase">Description</th>
                <th className="bg-gray-900 p-3 border-b border-gray-700 text-xs font-medium text-gray-400 uppercase text-center">Mandatory</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {requirements.map(r => (
                <tr key={r.id} className="hover:bg-gray-700/50 transition-colors">
                  <td className="p-3 border-b border-gray-700 font-semibold text-white">{r.requirement_key}</td>
                  <td className="p-3 border-b border-gray-700 font-semibold text-white">{r.label}</td>
                  <td className="p-3 border-b border-gray-700 text-gray-300">{r.description}</td>
                  <td className="p-3 border-b border-gray-700 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${r.mandatory ? 'bg-green-900/20 text-green-400 border-green-800' : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
                      {r.mandatory ? '✓ Yes' : '✗ No'}
                    </span>
                  </td>
                </tr>
              ))}
              {selected && requirements.length === 0 && (
                <tr><td colSpan={4} className="text-center py-12 text-gray-500">📝 No requirements defined</td></tr>
              )}
              {!selected && (
                <tr><td colSpan={4} className="text-center py-12 text-gray-500">🔍 Select a product to view requirements</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VisaProductsPage;
